/**
 * Woran das App-Update erkennt, dass gerade etwas in Arbeit ist — dann wird nicht neu geladen.
 *
 * Bewusst ohne DOM-Klassen (kein instanceof HTMLInputElement): Geprüft wird über Eigenschaften,
 * damit die Tests ohne jsdom auskommen.
 */

/** Grund, warum gerade nicht neu geladen wird — steht im Konsolen-Log. */
export type AppUpdateBusyReason =
  | 'offline'
  | 'pending-writes'
  | 'unsaved-input'
  | 'open-modal'
  | 'unload-guard';

// ─── Getippter Text ───────────────────────────────────────────────────────────

/**
 * Felder, in die man Text tippt. Schalter, Checkboxen und Datei-Auswahl zählen nicht: Ein
 * umgelegter Filter-Schalter hielte das Update sonst den ganzen Tag auf. Formulare, deren Zustand
 * nicht in Textfeldern steckt, melden sich über ihren beforeunload-Wächter (pageGuardsUnload).
 */
const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'tel',
  'url',
  'number',
  'password',
  'date',
  'datetime-local',
  'month',
  'time',
  'week',
]);

interface EditableLike {
  readonly isConnected?: boolean;
  readonly tagName?: string;
  readonly type?: string;
  readonly value?: string;
  readonly isContentEditable?: boolean;
  readonly textContent?: string | null;
}

function isTextField(el: EditableLike): boolean {
  const tag = el.tagName?.toUpperCase();
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') return TEXT_INPUT_TYPES.has((el.type || 'text').toLowerCase());
  return el.isContentEditable === true;
}

function textOf(el: EditableLike): string {
  const tag = el.tagName?.toUpperCase();
  return (tag === 'INPUT' || tag === 'TEXTAREA' ? el.value : el.textContent) ?? '';
}

/**
 * Merkt sich Textfelder, in die jemand getippt hat. Ungespeichert heißt: Das Feld hängt noch im
 * Dokument und ist nicht leer. Seite verlassen, Dialog nach dem Absenden zu, Feld geleert — dann
 * zählt es nicht mehr.
 *
 * Bewusst grob: Ein stehengelassener Suchbegriff hält das Update genauso auf wie ein halber
 * Kommentar. Andersherum wäre der Kommentar weg.
 */
export function createUnsavedInputTracker() {
  const edited = new Set<EditableLike>();

  const prune = () => {
    for (const el of edited) {
      if (!el.isConnected || textOf(el).trim() === '') edited.delete(el);
    }
  };

  return {
    /** Ziel eines input- oder change-Events. */
    record(target: unknown): void {
      if (!target || typeof target !== 'object') return;
      const el = target as EditableLike;
      if (!isTextField(el)) return;
      edited.add(el);
      if (edited.size > 50) prune();
    },

    hasUnsavedInput(): boolean {
      prune();
      return edited.size > 0;
    },
  };
}

// ─── Offene Dialoge ───────────────────────────────────────────────────────────

interface DialogQueryable {
  querySelectorAll(selectors: string): ArrayLike<{ getClientRects(): ArrayLike<unknown> }>;
}

/**
 * Offener modaler Dialog (Mantine Modal und Drawer setzen aria-modal). Unterschrift, Foto und
 * Scanner leben in Dialogen und hinterlassen keine Tipp-Spur — ein Reload schlösse sie. Nicht
 * modale Flächen (KIAN-Flyout, Popover) zählen nicht; keepMounted-Dialoge sind geschlossen
 * unsichtbar und haben keine Client-Rects.
 */
export function hasOpenModal(doc: DialogQueryable): boolean {
  return Array.from(doc.querySelectorAll('[aria-modal="true"]')).some(
    (el) => el.getClientRects().length > 0
  );
}

// ─── beforeunload-Wächter der Seite ──────────────────────────────────────────

/**
 * Fragt die Seite selbst: Würde sie beim Schließen warnen? Servicebericht und Lieferschein setzen
 * dafür einen beforeunload-Wächter, solange ein Entwurf offen ist — der kennt auch Unterschrift,
 * Fotos und Auswahlfelder ohne Tipp-Spur.
 *
 * Dafür wird beforeunload probeweise ausgelöst. Handler dürfen deshalb nichts tun, was nur beim
 * echten Verlassen passieren darf (CLAUDE.md „App-Update nie mitten in der Eingabe").
 */
export function pageGuardsUnload(target: Pick<EventTarget, 'dispatchEvent'>): boolean {
  const probe = new Event('beforeunload', { cancelable: true });
  target.dispatchEvent(probe);
  return probe.defaultPrevented;
}

// ─── Laufende Schreibanfragen ────────────────────────────────────────────────

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

/**
 * Zählt schreibende Anfragen, die noch unterwegs sind — ein Reload bräche sie ab, etwa das stille
 * Sichern beim Verlassen des Serviceberichts.
 *
 * Setzt an fetch selbst an statt an apiRequest: Streams, Blob-Downloads und das Kundenportal gehen
 * eigene Wege, und eine vergessene Stelle fiele erst auf, wenn Daten fehlen.
 */
export function trackPendingWrites(host: { fetch: typeof fetch }): { count(): number } {
  let pending = 0;
  const original = host.fetch.bind(host);

  host.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (READ_METHODS.has(methodOf(input, init))) return original(input, init);
    pending += 1;
    try {
      return original(input, init).finally(() => {
        pending -= 1;
      });
    } catch (err) {
      pending -= 1;
      throw err;
    }
  };

  return { count: () => pending };
}
